import { useTranslation } from "react-i18next";
import type { PeriodKind, PeriodQuery } from "../../api/reports";
import { Select } from "../Select";

/**
 * Period mesečnog izveštaja: mesec, kvartal, godina, kumulativ i slobodan raspon.
 *
 * **Zašto ne `presetRange` iz finansija.** Ono što tamo postoji je RELATIVNO — „ovaj mesec",
 * „prošli mesec", „ovaj kvartal" — i meri kako stoji sada. Izveštaj se pravi za IMENOVAN prošli
 * mesec i čita se mesecima kasnije, pa se avgust 2026. mora moći izabrati u novembru, što relativni
 * izbor ne ume. Zato je ovo svoja kontrola, a ne šesta upotreba one.
 *
 * Izbor živi u adresi, pa se tačno taj pogled može poslati linkom, a povratak iz izveštaja ne gubi
 * period.
 */

export interface PeriodPickerValue {
  kind: PeriodKind;
  year: number;
  month: number;
  quarter: number;
  asOf: string;
  from: string;
  to: string;
}

interface Props {
  value: PeriodPickerValue;
  onChange: (izmene: Partial<PeriodPickerValue>) => void;
  /** Najstarija i najnovija godina koju ledger nosi; spisak se ne izmišlja. */
  years: number[];
  disabled?: boolean;
}

const polje: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const natpis: React.CSSProperties = {
  fontSize: 11,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
};

export function ReportPeriodPicker({ value, onChange, years, disabled }: Props) {
  const { t } = useTranslation();

  const vrste: { id: PeriodKind; label: string }[] = [
    { id: "MONTH", label: t("report.period.month", "Mesec") },
    { id: "QUARTER", label: t("report.period.quarter", "Kvartal") },
    { id: "YEAR", label: t("report.period.year", "Godina") },
    { id: "YTD", label: t("report.period.ytd", "Od početka godine") },
    { id: "CUSTOM", label: t("report.period.custom", "Period od–do") },
  ];

  const meseci = [
    t("month.1", "januar"),
    t("month.2", "februar"),
    t("month.3", "mart"),
    t("month.4", "april"),
    t("month.5", "maj"),
    t("month.6", "jun"),
    t("month.7", "jul"),
    t("month.8", "avgust"),
    t("month.9", "septembar"),
    t("month.10", "oktobar"),
    t("month.11", "novembar"),
    t("month.12", "decembar"),
  ];

  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
      <div style={polje}>
        <span style={natpis}>{t("report.period.label", "Period")}</span>
        <Select
          value={value.kind}
          options={vrste.map((v) => ({ value: v.id, label: v.label }))}
          onChange={(kind) => onChange({ kind })}
          disabled={disabled}
          ariaLabel={t("report.period.label", "Period")}
          style={{ width: 190 }}
        />
      </div>

      {/* Godina stoji uz mesec, kvartal i godinu — ne uz kumulativ i slobodan raspon, koji nose
          svoje datume. Polje koje ne utiče na rezultat a stoji na ekranu je obećanje koje se ne
          ispunjava. */}
      {(value.kind === "MONTH" || value.kind === "QUARTER" || value.kind === "YEAR") && (
        <div style={polje}>
          <span style={natpis}>{t("report.period.yearLabel", "Godina")}</span>
          <Select
            value={String(value.year)}
            options={years.map((g) => ({ value: String(g), label: String(g) }))}
            onChange={(g) => onChange({ year: Number(g) })}
            disabled={disabled}
            ariaLabel={t("report.period.yearLabel", "Godina")}
            style={{ width: 110 }}
          />
        </div>
      )}

      {value.kind === "MONTH" && (
        <div style={polje}>
          <span style={natpis}>{t("report.period.monthLabel", "Mesec")}</span>
          <Select
            value={String(value.month)}
            options={meseci.map((ime, i) => ({ value: String(i + 1), label: ime }))}
            onChange={(m) => onChange({ month: Number(m) })}
            disabled={disabled}
            ariaLabel={t("report.period.monthLabel", "Mesec")}
            style={{ width: 150 }}
          />
        </div>
      )}

      {value.kind === "QUARTER" && (
        <div style={polje}>
          <span style={natpis}>{t("report.period.quarterLabel", "Kvartal")}</span>
          <Select
            value={String(value.quarter)}
            options={[1, 2, 3, 4].map((k) => ({
              value: String(k),
              label: t("report.period.quarterShort", { broj: k, defaultValue: "{{broj}}. kvartal" }),
            }))}
            onChange={(k) => onChange({ quarter: Number(k) })}
            disabled={disabled}
            ariaLabel={t("report.period.quarterLabel", "Kvartal")}
            style={{ width: 130 }}
          />
        </div>
      )}

      {value.kind === "YTD" && (
        <label style={polje}>
          <span style={natpis}>{t("report.period.asOf", "Do dana")}</span>
          <input
            type="date"
            className="input"
            style={{ width: 170 }}
            value={value.asOf}
            disabled={disabled}
            onChange={(e) => onChange({ asOf: e.target.value })}
          />
        </label>
      )}

      {value.kind === "CUSTOM" && (
        <>
          <label style={polje}>
            <span style={natpis}>{t("report.period.from", "Od")}</span>
            <input
              type="date"
              className="input"
              style={{ width: 170 }}
              value={value.from}
              disabled={disabled}
              onChange={(e) => onChange({ from: e.target.value })}
            />
          </label>
          <label style={polje}>
            <span style={natpis}>{t("report.period.to", "Do")}</span>
            <input
              type="date"
              className="input"
              style={{ width: 170 }}
              value={value.to}
              disabled={disabled}
              onChange={(e) => onChange({ to: e.target.value })}
            />
          </label>
        </>
      )}
    </div>
  );
}

/** Šta od izbora ide serveru. Server odbija nedostajući deo, pa se ovde ne pretpostavlja. */
export function periodQuery(v: PeriodPickerValue, country: string): PeriodQuery {
  const osnov: PeriodQuery = { kind: v.kind };
  if (country) osnov.country = country;
  switch (v.kind) {
    case "MONTH":
      return { ...osnov, year: v.year, month: v.month };
    case "QUARTER":
      return { ...osnov, year: v.year, quarter: v.quarter };
    case "YEAR":
      return { ...osnov, year: v.year };
    case "YTD":
      return { ...osnov, asOf: v.asOf };
    case "CUSTOM":
      return { ...osnov, from: v.from, to: v.to };
  }
}
